import { useState, useEffect, useRef } from 'react';
import InputArea from '../components/InputArea';
import ResponseArea from '../components/ResponseArea';
import SupportTab from '../components/SupportTab';
import HelpModal from '../components/HelpModal';
import { sendMessageStream, getChatId } from '../lib/api';
import { processResponse } from '../utils/responseProcessor';

/**
 * OraculoPage - Main SPA component
 * Manages chat state and UI rendering
 */
export default function OraculoPage() {
    const [input, setInput] = useState('');
    const [response, setResponse] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userQuestion, setUserQuestion] = useState<string | null>(null);

    // We use a ref to accumulate the streaming response without dependency issues
    const fullResponseRef = useRef('');

    // Focus input on load
    useEffect(() => {
        const input = document.getElementById('questionInput') as HTMLTextAreaElement;
        input?.focus();
    }, []);

    // Handle message submission
    const handleSubmit = async () => {
        const message = input.trim();
        if (!message || isLoading) return;

        setIsLoading(true);
        setResponse(''); // Start empty for stream
        fullResponseRef.current = ''; // Reset accumulator
        setUserQuestion(message); // Save the question to display

        // Clear input for next question
        setInput('');

        try {
            const chatId = getChatId();

            await sendMessageStream({
                chatId,
                message,
                route: 'general',
            }, (chunk) => {
                fullResponseRef.current += chunk;
                // Process on the fly to format links correctly
                const processed = processResponse(fullResponseRef.current);
                setResponse(processed);
            });

            // A successful but empty response would leave the screen blank and
            // make the question look like it simply vanished.
            if (!fullResponseRef.current.trim()) {
                setResponse('Não foi possível obter resposta do Oráculo. Por favor, tente novamente.');
            }

        } catch (error) {
            console.error('Chat error:', error);
            if (!fullResponseRef.current) {
                setResponse('Erro ao consultar o Oráculo. Por favor, tente novamente.');
            } else {
                setResponse(prev => (prev || '') + '\n\n❌ Erro de conexão interrompeu a resposta.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div id="oraculo-root">
            {/* Background */}
            <div className="background-container">
                <img
                    src="/images/background.webp"
                    alt="Background místico"
                    className="background-image"
                />
                <div className="background-overlay"></div>
            </div>

            {/* Main Content */}
            <div className="main-container">
                {/* Logo */}
                <div className="logo-container">
                    <img
                        src="/images/logo.webp"
                        alt="O Oráculo"
                        className="logo-image"
                    />
                </div>

                {/* Interaction Area */}
                <div className="interaction-container">
                    <InputArea
                        value={input}
                        onChange={setInput}
                        onSubmit={handleSubmit}
                        disabled={isLoading}
                    />

                    <ResponseArea
                        response={response}
                        isLoading={isLoading}
                        userQuestion={userQuestion}
                    />
                </div>
            </div>

            {/* Support Tab */}
            <SupportTab />

            {/* Help Button & Modal */}
            <HelpModal />
        </div>
    );
}
